import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLink, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkProps } from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusIcon from "@tSurya/Icons/MinusIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabTitle = "tabTitle",
    desc = "desc",
    numberOfTabs = "numberOfTabs",
    faqBoxes = "faqBoxes",
    cssClass = "cssClass",
    image = "image",
    newImage = "newImage",
    imgLink = "imgLink",
    label = "label",
    name = "name",
    description = "description",
    tabdescription = "tabdescription",
    internalListLinks = "internalListLinks",
    internalListTitle = "internalListTitle",
    internalListDescription = "internalListDescription",
    openInNewWidow = "openInNewWidow",
}

interface NameArray {
    name: string;
    tabdescription: string;
    faqBoxes: FAQModel[];
}

interface TabModel {
    fields: NameArray;
}
interface InternalListModel {
    fields: InternalListArray;
}

interface InternalListArray {
    internalListTitle: string;
    internalListDescription: string;
}

interface FAQArray {
    questions: string;
    answers: string;
    newImage: string;
    imgLink: string;
    title: string;
    label: string;
    link: LinkFieldValue;
    description: string;
    internalListLinks: InternalListModel[];
}

interface FAQModel {
    fields: FAQArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabTitle]: string;
        [fields.desc]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.faqBoxes]: FAQModel[];
        [fields.internalListLinks]: InternalListModel;
        [fields.cssClass]: string;
        [fields.tabdescription]: string;
        [fields.name]: string;
        [fields.openInNewWidow]: boolean;
    };
}

type Props = OwnProps;
export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    container?: InjectableCss;
    tabdesc?: InjectableCss;
    imageStyles?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    accordianContainer?: InjectableCss;
    accordianSection?: AccordionSectionPresentationProps;
    internalList?: InjectableCss;
    internalListTitle?: InjectableCss;
    internalListDescription?: InjectableCss;
    internalListContainer?: InjectableCss;
    ctaLink?: LinkProps;
    button?: InjectableCss;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.background")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            background-color: white;
            outline: 0;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            color: red;
            cursor: pointer;
            :focus {
                outline: 0;
            }
            /* border: 1px solid red; */
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="true"] {
                border-bottom: 2px solid ${getColor("text.main")};
                span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 700;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 600;
            font-size: 26px;
            line-height: 34px;
            text-align: center;
            color: ${getColor("text.main")};
            margin-bottom: 30px;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabdesc: {
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 25px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    css`
                        margin-bottom: 15px;
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    imageStyles: {
        css: css`
            max-width: 955px;
            max-height: 370px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 0px 20px;
                    `,
                    css`
                        padding: 0px 20px;
                    `,
                    css`
                        padding: 0px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    container: {
        css: css`
            max-width: 955px;
            margin: 0 auto;
            > div[class^="TabGroupWrapper"] {
                padding: 0px !important;
            }
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 21px;
                padding: 0px;
                z-index: 9;
                margin: 60px auto 35px;
                border: 1px solid blue;
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.base};
                    font-style: normal;
                    font-weight: 400;
                    font-size: 15px;
                    line-height: 24px;
                    color: ${getColor("text.main")};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
                max-width: 955px;
                border: 1px solid red;
                margin: 0 auto;
                margin-bottom: 60px;
            `,
        },
    },
    accordianContainer: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    accordianSection: {
        headerProps: {
            css: css`
                & button,
                & button:focus {
                    background-color: ${getColor("common.backgroundContrast")};

                    padding: 24px 20px;
                    border: 0;
                    > span:first-of-type {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 9px 20px 25px 40px;
                border: 0;
                &:not(dd[hidden]) {
                    display: flex;
                    display: flex;
                    align-items: center;
                }
                > span {
                    padding-left: 40px;
                    > p {
                        display: list-item;
                        margin-bottom: 0;
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.base};
                        color: ${getColor("text.accent")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        expandIconProps: {
            src: PlusIcon,
        },
        collapseIconProps: {
            src: MinusIcon,
        },
    },
    internalList: {
        css: css`
            max-width: 610px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            column-gap: 19px;
            margin-bottom: 58px;
            align-items: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    css`
                        flex-direction: column;
                        margin-bottom: 20px;
                        align-items: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    internalListContainer: {
        css: css`
            padding: 36px 0px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 18px 20px;
                    `,
                    css`
                        padding: 18px 20px;
                    `,
                    css`
                        padding: 18px 20px;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    internalListTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: ${getColor("text.main")};
        `,
    },
    internalListDescription: {
        css: css`
            max-width: 393px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: 400;
            font-size: 14px;
            line-height: 22px;
            color: ${getColor("text.accent")};
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    css`
                        max-width: 100%;
                        text-align: center;
                    `,
                    null,
                    null,
                ])}
        `,
    },
    button: {
        css: css`
            outline: 0;
            background-color: ${getColor("primary")};
            border: 1px solid ${getColor("primary")};
            padding: 10px 34px;
            width: max-content;
            max-width: 100%;
            /* display: block; */
            margin: 0 auto;
            transition: all 0.3s ease;
            cursor: pointer;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    css`
                        padding: 10px 22px;
                    `,
                    null,
                    null,
                ])}
            span {
                display: block;
                font-family: ${TYPOGRAPHY.fontFamily.base};
                font-style: normal;
                font-weight: 700;
                font-size: 16px;
                line-height: 24px;
                text-align: center;
                color: white !important;
                font-style: normal !important;
                transition: color 0.3s ease;
            }
            &:hover {
                background-color: ${getColor("common.background")};
                border-color: ${getColor("text.main")};
                span {
                    color: ${getColor("text.main")} !important;
                }
            }
        `,
    },
};

const styles = tabularDataStyles;

const SurRugGuidePads: React.FC<Props> = ({ fields, id }) => {
    const ctaLinks = useGetLinks(fields.faqBoxes, o => o.fields.link).links;
    const [expandedAccordian, setExpandedAccordian] = React.useState<number | null>(null);

    // const AllContent = [...fields.numberOfTabs];

    return (
        <StyledWrapper {...styles.container}>
            <Typography as="h2" {...styles.tabTitle}>
                {fields.name}
            </Typography>
            <Typography as="p" {...styles.tabdesc}>
                {fields.tabdescription}
            </Typography>
            <StyledWrapper>
                {fields.faqBoxes.map((faqs: FAQModel, index: number) => {
                    return (
                        // eslint-disable-next-line react/no-array-index-key, react/jsx-key
                        <StyledWrapper {...tabularDataStyles.accordianContainer} key={index}>
                            <LazyImage
                                src={faqs.fields.newImage}
                                {...styles.imageStyles}
                                altText={fields.name || "Main Banner"}
                            />
                            {faqs?.fields?.internalListLinks && (
                                <StyledWrapper {...styles.internalListContainer}>
                                    {faqs?.fields?.internalListLinks.map((item: InternalListModel, index: number) => {
                                        return (
                                            // eslint-disable-next-line react/no-array-index-key
                                            <StyledWrapper key={index} {...styles.internalList}>
                                                <StyledWrapper {...styles.internalListTitle}>
                                                    {item.fields.internalListTitle}
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.internalListDescription}>
                                                    {item.fields.internalListDescription}
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        );
                                    })}
                                </StyledWrapper>
                            )}
                            {faqs.fields.label && (
                                <Link
                                    href={ctaLinks[index].url}
                                    target={fields.openInNewWidow ? `_blank` : `_self`}
                                    {...styles.button}
                                    aria-label={faqs.fields.label || "Shop Our Pad"}
                                >
                                    {faqs.fields.label}
                                </Link>
                            )}
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: SurRugGuidePads,
    definition: {
        group: "Common",
        displayName: "SurRugGuidePads",
        fieldDefinitions: [
            {
                name: fields.name,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 0,
            },
            {
                name: fields.tabdescription,
                displayName: "Descriptioon",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.faqBoxes,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                defaultValue: [],
                sortOrder: 2,
                fieldType: "General",
                fieldDefinitions: [
                    {
                        displayName: "Image",
                        name: "newImage",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: fields.internalListLinks,
                        editorTemplate: "ListField",
                        getDisplay: (List: HasFields) => {
                            return List.fields.internalListTitle;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "internalListTitle",
                                displayName: "internalListTitle",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "internalListDescription",
                                displayName: "internalListDescription",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                    {
                        name: "link",
                        displayName: "Button Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "label",
                        displayName: "Button Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWidow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default widgetModule;
